import { useContext, useState, useEffect } from "react";
import { PlatformContext } from "./Contexts";
import { getFrontendSettingsRequest } from "services/frontendSettingsRequests";

interface StoredSettings {
  expiresAt: number;
  content?: any;
}

// IndexedDB helper functions
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("platformDB", 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event: any) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains("frontendSettings")) {
        db.createObjectStore("frontendSettings");
      }
    };
  });
};

const getFromDB = async (key: string): Promise<StoredSettings | null> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = (db as IDBDatabase).transaction(
      ["frontendSettings"],
      "readonly"
    );
    const store = transaction.objectStore("frontendSettings");
    const request = store.get(key);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const saveToDB = async (key: string, value: any) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = (db as IDBDatabase).transaction(
      ["frontendSettings"],
      "readwrite"
    );
    const store = transaction.objectStore("frontendSettings");
    const request = store.put(value, key);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export function PlatformProvider({ children }: any) {
  // The platform string is also stored in the theme but is stored as its own state for convenience
  const [platform, setPlatform] = useState("");
  const [title, setTitle] = useState(""); // same as platform but formatted
  const [frontendSettings, setFrontendSettings] = useState({});
  const [landingBlurb, setLandingBlurb] = useState("");
  const [favicon, setFavicon] = useState();
  const [logo, setLogo] = useState();
  const [feedbucketKey, setFeedbucketKey] = useState("");
  const [helpCenter, setHelpCenter] = useState("");
  const [loginBackground, setLoginBackground] = useState();
  const [licensesEmail, setLicensesEmail] = useState("");
  const [knowledgeWatermark, setKnowledgeWatermark] = useState();
  const [knowledgeBadge, setKnowledgeBadge] = useState();
  const [badgesWatermark, setBadgesWatermark] = useState();
  const [assistantWatermark1, setAssistantWatermark1] = useState();
  const [assistantWatermark2, setAssistantWatermark2] = useState();
  const [learningJourneyWatermark, setLearningJourneyWatermark] = useState();
  const [quizWatermark1, setQuizWatermark1] = useState();
  const [quizWatermark2, setQuizWatermark2] = useState();
  const [assistantImages, setAssistantImages] = useState();
  const [assistants, setAssistants] = useState();
  const [classesTerminology, setClassesTerminology] = useState();
  const [studentsTerminology, setStudentsTerminology] = useState();
  const [dataRequirements, setDataRequirements] = useState([]);
  const [lessonCategories, setLessonCategories] = useState([]);
  const [schoolTerminology, setSchoolTerminology] = useState();
  const [limitCodeAttempts, setLimitCodeAttempts] = useState();
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState();
  const [termsOfUseUrl, setTermsOfUseUrl] = useState();
  const [supportUrl, setSupportUrl] = useState();
  const [st4sBadge, setSt4sbadge] = useState();
  const [poweredByEdtech, setPoweredByEdtech] = useState();

  const [loading, setLoading] = useState(true);
  const idleTimeout = 60 * 60 * 1000; //ms

  const EXPIRATION_TIME = 24 * 60 * 60 * 1000;

  async function loadFrontendSettings() {
    try {
      // Try to get from IndexedDB first
      const storedSettings = await getFromDB("frontendSettings");

      // If found in IndexedDB, parse and apply it if not yet expired
      if (storedSettings && Date.now() < storedSettings.expiresAt) {
        applyTheme(storedSettings);
        return storedSettings;
      }

      // If not in IndexedDB or expired, fetch from API
      const response = await getFrontendSettingsRequest();
      if (!response?.content) {
        throw new Error("No data received from API");
      }

      // Save to IndexedDB and return
      const newSettings = {
        ...response.content,
        expiresAt: Date.now() + EXPIRATION_TIME,
      };
      await saveToDB("frontendSettings", newSettings);
      applyTheme(newSettings);
      return newSettings;
    } catch (error) {
      console.error("Failed to load frontend settings:", error);
      return {};
    }
  }

  // Function to apply colour theme by overwriting CSS vars in tailwind config
  function applyTheme(frontendSettings: any) {
    if (!frontendSettings?.colors) return;

    // Iterate through each color category (primary, secondary, etc.)
    Object.entries(frontendSettings.colors).forEach(([category, shades]) => {
      // Iterate through each shade in the category
      Object.entries(shades as Record<string, string>).forEach(
        ([shade, value]) => {
          if (typeof value === "string") {
            document.documentElement.style.setProperty(
              `--color-${category}-${shade}`,
              value
            );
          }
        }
      );
    });
  }

  // Load the theme
  useEffect(() => {
    loadFrontendSettings().then((newSettings) => {
      setFrontendSettings(newSettings);
      setPlatform(newSettings.platform);
      setTitle(newSettings.title);
      setLandingBlurb(newSettings.blurb);
      setFavicon(newSettings.favicon);
      setLogo(newSettings.logo);
      setFeedbucketKey(newSettings.feedbucketKey);
      setHelpCenter(newSettings.helpCenter);
      setLoginBackground(newSettings.loginBackground);
      setLicensesEmail(newSettings.licensesEmail);
      setKnowledgeWatermark(newSettings.knowledgeWatermark);
      setKnowledgeBadge(newSettings.knowledgeBadge);
      setBadgesWatermark(newSettings.badgesWatermark);
      setAssistantWatermark1(newSettings.assistantWatermark1);
      setAssistantWatermark2(newSettings.assistantWatermark2);
      setLearningJourneyWatermark(newSettings.learningJourneyWatermark);
      setQuizWatermark1(newSettings.quizWatermark1);
      setQuizWatermark2(newSettings.quizWatermark2);
      setAssistantImages(newSettings.assistantImages);
      setAssistants(newSettings.assistants);
      setClassesTerminology(newSettings.classesTerminology || "Classes");
      setStudentsTerminology(newSettings.studentsTerminology || "Students");
      setDataRequirements(newSettings.dataRequirements);
      setLessonCategories(newSettings.lessonCategories);
      setSchoolTerminology(newSettings.schoolTerminology || "School");
      setLimitCodeAttempts(newSettings.limitCodeAttempts);
      setPrivacyPolicyUrl(newSettings.privacyPolicyUrl);
      setTermsOfUseUrl(newSettings.termsOfUseUrl);
      setSupportUrl(newSettings.supportUrl);
      setSt4sbadge(newSettings.st4sBadge);
      setPoweredByEdtech(newSettings.poweredByEdtech);
      setLoading(false);
    });
  }, []);

  // Early return to prevent displaying page with default theme while its loading
  if (loading) {
    return <></>;
  }

  return (
    <PlatformContext.Provider
      value={{
        platform,
        title,
        landingBlurb,
        favicon,
        logo,
        idleTimeout,
        feedbucketKey,
        helpCenter,
        loginBackground,
        licensesEmail,
        knowledgeWatermark,
        knowledgeBadge,
        badgesWatermark,
        assistantWatermark1,
        assistantWatermark2,
        learningJourneyWatermark,
        quizWatermark1,
        quizWatermark2,
        assistantImages,
        assistants,
        classesTerminology,
        studentsTerminology,
        dataRequirements,
        lessonCategories,
        schoolTerminology,
        limitCodeAttempts,
        privacyPolicyUrl,
        termsOfUseUrl,
        st4sBadge,
        poweredByEdtech,
        supportUrl
      }}
    >
      {children}
    </PlatformContext.Provider>
  );
}

// Custom hook to use the theme
export function usePlatform() {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error("usePlatform must be used within a PlatformProvider");
  }
  return context;
}
